import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLeadCapture } from '@/hooks/useLeadCapture';
import { Phone, Envelope as Mail, Buildings as Building, ChatText as MessageSquare } from '@phosphor-icons/react';
import { z } from 'zod';
import { useToast } from '@/hooks/use-toast';
import { useCountry } from '@/hooks/useCountry';

const leadFormSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  email: z.string().trim().email('Invalid email address').max(255, 'Email must be less than 255 characters'),
  phone: z.string().trim().max(20, 'Phone must be less than 20 characters').optional(),
  company: z.string().trim().max(200, 'Company name must be less than 200 characters').optional(),
  message: z.string().trim().max(1000, 'Message must be less than 1000 characters').optional(),
});

interface LeadCaptureFormProps {
  variant?: 'full' | 'dealer' | 'quick';
  source: string;
  title?: string;
  subtitle?: string;
  defaultLeadType?: 'dealer' | 'seller' | 'buyer' | 'demo_request' | 'consultation' | 'general';
  className?: string;
  listingId?: string;
}

export const LeadCaptureForm = ({ 
  variant = 'full', 
  source, 
  title,
  subtitle,
  defaultLeadType = 'general',
  className = '',
  listingId
}: LeadCaptureFormProps) => {
  const { country } = useCountry();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    lead_type: defaultLeadType,
    message: '',
    budget_range: '',
    car_interest: ''
  });

  const { captureLead, isSubmitting } = useLeadCapture();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    try {
      leadFormSchema.parse({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        company: formData.company || undefined,
        message: formData.message || undefined,
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
        return;
      }
    }
    
    const success = await captureLead({
      ...formData,
      source,
      lead_type: formData.lead_type as any,
      budget_range: formData.budget_range as any,
      listing_id: listingId
    });

    if (success) {
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        lead_type: defaultLeadType,
        message: '',
        budget_range: '',
        car_interest: ''
      });
    }
  };

  if (variant === 'quick') {
    return (
      <Card className={`${className} border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10`}>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">
                {title || "📞 Solicită o consultație GRATUITĂ"}
              </h3>
              {subtitle && (
                <p className="text-sm text-muted-foreground mb-4">{subtitle}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                placeholder="Numele tău *"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
              <Input
                type="email"
                placeholder="Email *"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
            </div>
            
            <Input
              type="tel"
              placeholder="Telefon (opțional)"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
            />
            
            <Button 
              type="submit" 
              disabled={isSubmitting || !formData.name || !formData.email}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isSubmitting ? '⏳ Se trimite...' : '🚀 Solicită consultația'}
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'dealer') {
    return (
      <Card className={`${className} border-primary/20`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="text-primary" size={20} />
            {title || "Demonstrație GRATUITĂ pentru Dealeri"}
          </CardTitle>
          {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  placeholder={country === 'UK' ? 'Your name *' : country === 'DE' ? 'Ihr Name *' : 'Numele tău *'}
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  maxLength={100}
                  required
                />
              </div>
              <div>
                <Input
                  placeholder={country === 'UK' ? 'Dealership name *' : country === 'DE' ? 'Händlername *' : 'Numele dealership-ului *'}
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  maxLength={200}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="email"
                placeholder={country === 'UK' ? 'Business email *' : country === 'DE' ? 'Geschäfts-E-Mail *' : 'Email business *'}
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                maxLength={255}
                required
              />
              <Input
                type="tel"
                placeholder={country === 'UK' ? 'Phone *' : country === 'DE' ? 'Telefon *' : 'Telefon *'}
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                maxLength={20}
                required
              />
            </div>
            
            <Select onValueChange={(value) => setFormData({...formData, budget_range: value})}>
              <SelectTrigger>
                <SelectValue placeholder={country === 'UK' ? 'How many cars do you sell per month?' : country === 'DE' ? 'Wie viele Autos verkaufen Sie pro Monat?' : 'Câte mașini vinzi pe lună?'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="under_10k">{country === 'UK' ? 'Under 10 cars' : country === 'DE' ? 'Unter 10 Autos' : 'Sub 10 mașini'}</SelectItem>
                <SelectItem value="10k_25k">{country === 'UK' ? '10-25 cars' : country === 'DE' ? '10-25 Autos' : '10-25 mașini'}</SelectItem>
                <SelectItem value="25k_50k">{country === 'UK' ? '25-50 cars' : country === 'DE' ? '25-50 Autos' : '25-50 mașini'}</SelectItem>
                <SelectItem value="over_50k">{country === 'UK' ? 'Over 50 cars' : country === 'DE' ? 'Über 50 Autos' : 'Peste 50 mașini'}</SelectItem>
              </SelectContent>
            </Select>
            
            <Textarea
              placeholder={country === 'UK' ? 'What interests you the most? (optional)' : country === 'DE' ? 'Was interessiert Sie am meisten? (optional)' : 'Ce te interesează cel mai mult? (opțional)'}
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              maxLength={1000}
              rows={3}
            />
            
            <Button 
              type="submit" 
              disabled={isSubmitting || !formData.name || !formData.email || !formData.company}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              size="lg"
              onClick={(e) => {
                // Also open email client for direct scheduling
                if (!isSubmitting && formData.name && formData.email && formData.company) {
                  // Sanitize and encode data properly
                  const sanitizedName = encodeURIComponent(formData.name.substring(0, 100));
                  const sanitizedCompany = encodeURIComponent(formData.company.substring(0, 200));
                  const sanitizedEmail = encodeURIComponent(formData.email.substring(0, 255));
                  const sanitizedPhone = encodeURIComponent((formData.phone || '').substring(0, 20));
                  const sanitizedBudget = encodeURIComponent(formData.budget_range ? formData.budget_range.replace('_', '-') : 'Not specified');
                  const sanitizedMessage = encodeURIComponent((formData.message || 'Not specified').substring(0, 500));
                  
                  const emailSubject = country === 'UK' 
                    ? `FREE Demo Request - ${sanitizedCompany}` 
                    : country === 'DE'
                    ? `KOSTENLOSE Demo-Anfrage - ${sanitizedCompany}`
                    : `Programare Consultație GRATUITĂ - ${sanitizedCompany}`;
                  
                  const emailBody = country === 'UK'
                    ? `Hello,%0A%0AI would like to schedule a FREE 15-minute demo to learn how to increase my sales by 40%%.%0A%0ADetails:%0AName: ${sanitizedName}%0ADealership: ${sanitizedCompany}%0AEmail: ${sanitizedEmail}%0APhone: ${sanitizedPhone}%0ACars per month: ${sanitizedBudget}%0AInterests: ${sanitizedMessage}%0A%0AThank you!%0A%0A----%0ASent via RO Auto Market`
                    : country === 'DE'
                    ? `Guten Tag,%0A%0AIch möchte eine KOSTENLOSE 15-minütige Demo buchen, um zu erfahren, wie ich meine Verkäufe um 40%% steigern kann.%0A%0ADetails:%0AName: ${sanitizedName}%0AHändler: ${sanitizedCompany}%0AE-Mail: ${sanitizedEmail}%0ATelefon: ${sanitizedPhone}%0AAutos pro Monat: ${sanitizedBudget}%0AInteressen: ${sanitizedMessage}%0A%0AVielen Dank!%0A%0A----%0AGesendet über RO Auto Market`
                    : `Bună ziua,%0A%0AÎmi doresc să programez o consultație GRATUITĂ de 15 minute pentru a afla cum pot să îmi cresc vânzările cu 40%%.%0A%0ADetalii:%0ANume: ${sanitizedName}%0ADealership: ${sanitizedCompany}%0AEmail: ${sanitizedEmail}%0ATelefon: ${sanitizedPhone}%0AMașini pe lună: ${sanitizedBudget}%0AInterese: ${sanitizedMessage}%0A%0AVă mulțumesc!%0A%0A----%0ATrimis prin RO Auto Market`;
                  
                  setTimeout(() => {
                    window.open(`mailto:vladimir@tudorseo.com?subject=${emailSubject}&body=${emailBody}`, '_blank');
                  }, 1000); // Delay to allow lead submission first
                }
              }}
            >
              {isSubmitting ? (country === 'UK' ? '⏳ Scheduling...' : country === 'DE' ? '⏳ Wird geplant...' : '⏳ Se programează...') : (country === 'UK' ? '📅 Schedule FREE Demo' : country === 'DE' ? '📅 KOSTENLOSE Demo buchen' : '📅 Programează demonstrația GRATUITĂ')}
            </Button>
            
            <p className="text-xs text-muted-foreground text-center">
              {country === 'UK' ? '✅ 15-minute demo • ✅ No obligations • ✅ Full setup included' : country === 'DE' ? '✅ 15-Minuten-Demo • ✅ Keine Verpflichtungen • ✅ Komplettes Setup inklusive' : '✅ Demonstrație de 15 minute • ✅ Fără obligații • ✅ Setup complet inclus'}
            </p>
          </form>
        </CardContent>
      </Card>
    );
  }

  // Full form variant
  return (
    <Card className={`${className} border-primary/20`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="text-primary" size={20} />
          {title || "Contactează-ne"}
        </CardTitle>
        {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              placeholder="Numele tău *"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
            <Input
              placeholder="Compania (opțional)"
              value={formData.company}
              onChange={(e) => setFormData({...formData, company: e.target.value})}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                type="email"
                placeholder="Email *"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="pl-10"
                required
              />
            </div>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                type="tel"
                placeholder="Telefon (opțional)"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="pl-10"
              />
            </div>
          </div>
          
          <Select onValueChange={(value) => setFormData({...formData, lead_type: value as any})}>
            <SelectTrigger>
              <SelectValue placeholder="Cu ce te putem ajuta?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dealer">Sunt dealer - vreau demonstrație</SelectItem>
              <SelectItem value="seller">Vreau să îmi vând mașina</SelectItem>
              <SelectItem value="buyer">Caut o mașină</SelectItem>
              <SelectItem value="consultation">Vreau consultanță</SelectItem>
              <SelectItem value="general">Întrebare generală</SelectItem>
            </SelectContent>
          </Select>
          
          {formData.lead_type === 'buyer' && (
            <Select onValueChange={(value) => setFormData({...formData, budget_range: value})}>
              <SelectTrigger>
                <SelectValue placeholder="Bugetul tău aproximativ?" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="under_10k">Sub 10.000€</SelectItem>
                <SelectItem value="10k_25k">10.000€ - 25.000€</SelectItem>
                <SelectItem value="25k_50k">25.000€ - 50.000€</SelectItem>
                <SelectItem value="over_50k">Peste 50.000€</SelectItem>
              </SelectContent>
            </Select>
          )}
          
          <Textarea
            placeholder="Mesajul tău (opțional)"
            value={formData.message}
            onChange={(e) => setFormData({...formData, message: e.target.value})}
            rows={4}
          />
          
          <Button 
            type="submit" 
            disabled={isSubmitting || !formData.name || !formData.email}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            size="lg"
          >
            {isSubmitting ? '⏳ Se trimite...' : '📩 Trimite mesajul'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
